import * as stateTypes from 'app/pages/DiscountEditor/modules/ProductSelection/state';
import {Action, ActionType} from 'app/pages/DiscountEditor/actionTypes';
import { combineReducers } from 'redux';
import { ProductType, Product } from 'app/shared/model/Product';
import { ProductOfferingsMap, OfferingLoadState, LoadedOfferingDetails, SchoolSelectionGroup, SchoolOption, SchoolGroupMap, ProductMap } from 'app/pages/DiscountEditor/modules/ProductSelection/state';
import { ProductOffering } from 'app/shared/model/ProductOffering';
import { ProductFilterType, ProductFilter, SelectionMap, setProductType, setProductFilterType, changeProductSelection, parseProductFilterDto } from 'app/shared/model/ProductFilter';
import { Centre } from 'app/shared/model';
import { UnitsFilterType, UnitsFilter, parseUnitsFilterDto } from 'app/shared/model/ProductUnitFilter';

const defaultFilter : ProductFilter =  {
    type: ProductFilterType.AnyProduct
};

const defaultUnitsFilter : UnitsFilter = {
    type: UnitsFilterType.None
}

const defaultState : stateTypes.ProductSelectionState = {
    productFilter: defaultFilter,
    centresMap: {},
    schoolFilter: {},
    unitsFilter: defaultUnitsFilter,
    allSchools: {},
    schoolGroups: {},
    centreGroupOpenState: {},
    allCurrencies: [],
    offeringsBySchool: {},
    allProducts: {},
    productsInCurrentSchools: new Set<string>(),
    catalogLoadState: stateTypes.OfferingLoadState.None
}

export function reducer(state: stateTypes.ProductSelectionState|undefined, action: Action) : stateTypes.ProductSelectionState {
    if (state == undefined)
        state = defaultState;

    switch(action.type) {

        case ActionType.ChangeProductFilter:
            return {
                ... state,
                productFilter: action.productFilter
            }

        case ActionType.ChangeSchoolSelection:
            const groupCode = state.allSchools[action.code].countryCode;
            const [
                newFilter,
                newGroups ] = changeSchoolSelection(state.schoolFilter, state.schoolGroups, action.code, groupCode, action.isSelected);

            const productsInCurrentSchools = buildProductOptions(state.allProducts);

            const offeringsBySchool : ProductOfferingsMap = state.offeringsBySchool[action.code]
                ? state.offeringsBySchool
                : {[action.code]: {loadState: stateTypes.OfferingLoadState.None}, ...state.offeringsBySchool};

            return {
                ...state,
                schoolFilter: newFilter,
                schoolGroups: newGroups,
                productsInCurrentSchools: productsInCurrentSchools,
                offeringsBySchool: offeringsBySchool
            };

        case ActionType.ChangeSchoolGroupSelection:
        {
            const newGroup : stateTypes.SchoolSelectionGroup = {... state.schoolGroups[action.code]}
            if (action.isSelected) {
                newGroup.itemsSelected += newGroup.itemsNotSelected;
                newGroup.itemsNotSelected = 0;
            }
            else {
                newGroup.itemsNotSelected += newGroup.itemsSelected;
                newGroup.itemsSelected = 0;
            }

            const newSchoolFilter : SelectionMap = {}
            Object.keys(state.allSchools)
                .map(k => state && state.allSchools[k])
                .forEach(item => {
                    if (!item || !state)
                        return;
                    if (action.isSelected && item.countryCode === action.code)
                        newSchoolFilter[item.code] = true;
                    else if (item.countryCode !== action.code && state.schoolFilter[item.code])
                        newSchoolFilter[item.code] = true
                });

            const productOptions =
             buildProductOptions(state.allProducts);

            const offeringsBySchool : ProductOfferingsMap = {...state.offeringsBySchool};
            Object.keys(newSchoolFilter).forEach(schoolCode => {
                if (!offeringsBySchool[schoolCode]) {
                    offeringsBySchool[schoolCode] = {loadState: OfferingLoadState.None}
                }
            });

            return {
                ... state,
                offeringsBySchool: offeringsBySchool,
                productsInCurrentSchools: productOptions,
                schoolFilter: newSchoolFilter,
                schoolGroups: {
                    ... state.schoolGroups,
                    [newGroup.groupCode]: newGroup
                }
            };

        }

        case ActionType.ChangeSchoolGroupOpenState:
            return {
                ... state,
                schoolGroups: {
                    ... state.schoolGroups,
                    [action.code]: {
                        ... state.schoolGroups[action.code],
                        isOpen: action.isOpen
                    }
                }
            }

        case ActionType.ChangeCentreGroupOpenState:
            return {
                ... state,
                centreGroupOpenState: {
                    ... state.centreGroupOpenState,
                    [action.centreCode]: action.isOpen
                }
            }

        case ActionType.ChangeCentreGroupSelection:
        {
            const schoolsInCentre = Object.keys(state.allSchools)
                .map(k => state.allSchools[k])
                .filter(s => s.school.centreCode === action.centreCode);
            const schoolCodesInCentre = schoolsInCentre.map(s => s.code);

            const newSchoolFilter : SelectionMap = { ...state.schoolFilter };
            schoolCodesInCentre.forEach(code => {
                if (action.isSelected) {
                    newSchoolFilter[code] = true;
                } else {
                    delete newSchoolFilter[code];
                }
            });

            const countryCode = schoolsInCentre[0]?.countryCode;
            const newSchoolGroups = { ...state.schoolGroups };
            if (countryCode && newSchoolGroups[countryCode]) {
                const countryGroup = { ...newSchoolGroups[countryCode] };
                const delta = action.isSelected ? schoolsInCentre.length : -schoolsInCentre.length;
                countryGroup.itemsSelected += delta;
                countryGroup.itemsNotSelected -= delta;
                newSchoolGroups[countryCode] = countryGroup;
            }

            const productOptions = buildProductOptions(state.allProducts);
            const offeringsBySchool : ProductOfferingsMap = { ...state.offeringsBySchool };
            schoolCodesInCentre.forEach(schoolCode => {
                if (!offeringsBySchool[schoolCode]) {
                    offeringsBySchool[schoolCode] = { loadState: OfferingLoadState.None };
                }
            });

            return {
                ... state,
                schoolFilter: newSchoolFilter,
                schoolGroups: newSchoolGroups,
                productsInCurrentSchools: productOptions,
                offeringsBySchool: offeringsBySchool
            };
        }

        case ActionType.ChangeUnitFilter:
            return {
                ... state,
                unitsFilter: action.filter
            }

        case ActionType.CatalogProductsLoaded:
        {
            const allProductsMap: ProductMap = {};
            action.products.forEach((p) => {
                allProductsMap[p.code] = p;
            });

            let offeringsBySchool = state.offeringsBySchool;
            if (action.loadedSchoolCodes) {
                offeringsBySchool = { ...state.offeringsBySchool };
                action.loadedSchoolCodes.forEach((schoolCode) => {
                    offeringsBySchool[schoolCode] = {
                        loadState: OfferingLoadState.Loaded,
                        productCodes: [],
                    };
                });
                const loadedOfferings = createOfferingsMap(action.offerings || []);
                Object.keys(loadedOfferings).forEach((schoolCode) => {
                    offeringsBySchool[schoolCode] = loadedOfferings[schoolCode];
                });
            }

            return {
                ...state,
                allProducts: allProductsMap,
                productsInCurrentSchools: buildProductOptions(allProductsMap),
                offeringsBySchool,
                catalogLoadState: OfferingLoadState.Loaded,
            };
        }

        case ActionType.PageLoaded:
            const centresMap : {[id: string]: Centre} = {};
            action.data.centres.forEach(c =>
                centresMap[c.code] = c
            );

            const allSchools : stateTypes.SchoolOptionMap = {}
            const newSchoolGroups : stateTypes.SchoolGroupMap = {};
            const noCountryGroup = {groupCode: '', title: 'Unknown country', isSelected: false, isIndeterminiate: false}
            action.data.schools.map(school => {
                const centre = centresMap[school.centreCode];
                const country = centre ? action.data.countries.find(country => country.id == centre.countryCode) : undefined;
                const groupCode = country ? country.id : '';
                let groupRec = newSchoolGroups[groupCode];

                allSchools[school.code] = {
                    name: school.name,
                    code: school.code,
                    currencyCode: centre ? centre.currencyCode : '',
                    countryCode: centre ? centre.countryCode : '',
                    school: school
                };

                if (!groupRec) {
                    groupRec = {
                        groupCode: groupCode,
                        title: country ? country.name : 'Unknown',
                        itemsNotSelected: 1,
                        isOpen: false,
                        itemsSelected: 0
                    };
                    newSchoolGroups[groupCode] = groupRec;
                }
                else {
                    ++groupRec.itemsNotSelected;
                }
            })

            const newSchoolFilter : SelectionMap = {};
            action.discount.schoolsFilter.schoolCodes.forEach(apiSchoolCode => {
                let school = allSchools[apiSchoolCode];
                let matchedSchoolCode = apiSchoolCode;
                
                if (!school) {
                    let numericId: number | null = null;
                    const directParse = parseInt(apiSchoolCode, 10);
                    if (!isNaN(directParse)) {
                        numericId = directParse;
                    } else {
                        const numberMatch = apiSchoolCode.match(/\d+/);
                        if (numberMatch) {
                            numericId = parseInt(numberMatch[0], 10);
                        }
                    }
                    
                    if (numericId !== null && !isNaN(numericId)) {
                        const allMatches = Object.entries(allSchools).filter(([code, schoolOption]) => {
                            const codeNumeric = parseInt(code.replace(/\D/g, ''), 10);
                            return !isNaN(codeNumeric) && codeNumeric === numericId;
                        });
                        
                        if (allMatches.length > 0) {
                          let match = allMatches[0];
                            
                            if (allMatches.length > 1 && apiSchoolCode.includes('-')) {
                                const prefix = apiSchoolCode.split('-')[0].toUpperCase();
                             }
                            
                            matchedSchoolCode = match[0];
                            school = match[1];
                        }
                    } else {
                    }
                }
                
                if (school) {
                    const group = newSchoolGroups[school.countryCode];
                    if (group) {
                        group.itemsNotSelected -= 1;
                        group.itemsSelected += 1;
                    }
                    newSchoolFilter[matchedSchoolCode] = true;
                }
            });

            const newUnitFilter = parseUnitsFilterDto(action.discount.unitsFilter); 
            const newOfferingsMap = createOfferingsMap(action.data.offerings);
            let newProductFilter = parseProductFilterDto(action.discount.productFilter);

            const allProducts : ProductMap = {};
            action.data.products.forEach(p =>
                allProducts[p.code] = p
            );

            const newProductsInCurrentSchools = buildProductOptions(allProducts)

            return {
                ... state,
                schoolGroups: newSchoolGroups,
                schoolFilter: newSchoolFilter,
                allProducts: allProducts,
                allSchools: allSchools,
                centreGroupOpenState: {},
                allCurrencies: action.data.currencies,
                centresMap: centresMap,
                unitsFilter: newUnitFilter,
                offeringsBySchool: newOfferingsMap,
                productsInCurrentSchools: newProductsInCurrentSchools,
                productFilter: newProductFilter,
                catalogLoadState: OfferingLoadState.None
            }

        case ActionType.OfferingsLoadStarted:
            return {
                ...setOfferingsLoadState(state, action.schoolIds, OfferingLoadState.Loading),
                catalogLoadState: OfferingLoadState.Loading
            };
        case ActionType.OfferingsLoadError:
            return {
                ...setOfferingsLoadState(state, action.schoolIds, OfferingLoadState.Errored),
                catalogLoadState: OfferingLoadState.Errored
            };
        case ActionType.OfferingsInvalidateForSchools:
            return setOfferingsLoadState(state, action.schoolIds, OfferingLoadState.None);

        case ActionType.AddOfferingsForSchool:
        {
            const newSchoolOfferings = createOfferingsMap(action.offerings);
            const offeringsBySchool : ProductOfferingsMap = {
                ... state.offeringsBySchool,
                ... newSchoolOfferings
            }

            const productsInCurrentSchools = buildProductOptions(state.allProducts);

            return {
                ... state,
                offeringsBySchool,
                productsInCurrentSchools: productsInCurrentSchools
            }
        }

        default:
            if ((action as any).type === "DISCOUNT/RESET_APPLICABLE_PRODUCTS") {
                return {
                    ...state,
                    productFilter: defaultFilter
                };
            }
            return state;
    }
}

function isProductActiveForDiscount(product: Product): boolean {
    const a = (product as { active?: boolean }).active;
    return a !== false;
}

function buildProductOptions(allProducts: stateTypes.ProductMap): Set<string> {
    const codesIncluded = new Set<string>();
    Object.keys(allProducts).forEach(code => {
        const p = allProducts[code];
        if (p && isProductActiveForDiscount(p)) {
            codesIncluded.add(code);
        }
    });
    
    return codesIncluded;
}

function createOfferingsMap(offerings: ProductOffering[]) : ProductOfferingsMap {
    type LoadedSchool = {
        [school:string]: (LoadedOfferingDetails|undefined)};
    const result: LoadedSchool = {};
    const schoolGroups: {[schoolId: string]: string[]} = {};
    offerings.forEach(x => {
        if (!schoolGroups[x.schoolId]) {
            schoolGroups[x.schoolId] = [];
        }
        schoolGroups[x.schoolId].push(x.productId);
    });
    
    Object.keys(schoolGroups).forEach(schoolId => {
        const productCodes = schoolGroups[schoolId];
        const entry: LoadedOfferingDetails = {
            loadState: OfferingLoadState.Loaded,
            productCodes: productCodes
        };
        
        result[schoolId] = entry;
        
        const numericMatch = schoolId.match(/\d+/);
        if (numericMatch) {
            const numericId = numericMatch[0];
            if (numericId !== schoolId && !result[numericId]) {
                result[numericId] = entry;
            }
        }
    });
    
    return result;
}

function setOfferingsLoadState(state: stateTypes.ProductSelectionState, schoolIds: string[], loadState: OfferingLoadState.Errored|OfferingLoadState.Loading|OfferingLoadState.None) {
    const loadingStates : ProductOfferingsMap = schoolIds.reduce(
        (map: stateTypes.ProductOfferingsMap, schoolCode: string) => {
            map[schoolCode] = {loadState: loadState};
            return map;
        },
        {}
    );
    const existingOfferings = state.offeringsBySchool;
    Object.keys(existingOfferings).forEach((schoolCode: string) => {
        if (!loadingStates[schoolCode])
            loadingStates[schoolCode] = existingOfferings[schoolCode];
    });

    return {
        ... state,
        offeringsBySchool: loadingStates
    }
}


function changeSchoolSelection(filter: SelectionMap, groups: SchoolGroupMap, code: string, groupCode: string, isSelected: boolean)
 : [SelectionMap, SchoolGroupMap] {
    const newSelection = isSelected
        ? addSelection(filter, code)
        : removeSelection(filter, code);

    if (newSelection != filter) {
        const thisGroup = {...groups[groupCode]};
        thisGroup.itemsNotSelected = thisGroup.itemsNotSelected + (isSelected ? -1 : 1);
        thisGroup.itemsSelected = thisGroup.itemsSelected + (isSelected ? 1 : -1);
        groups = {
            ...groups,
            [groupCode]: thisGroup
        }
    }

    return [newSelection, groups];
}

function addSelection(selectionMap: SelectionMap, code: string) : SelectionMap {
    if (selectionMap[code])
        return selectionMap;

    return {
        ... selectionMap,
        [code]: true
    }
}

function removeSelection(selectionMap: SelectionMap, code: string) : SelectionMap {
    const newCodes : SelectionMap = { };
    Object.keys(selectionMap).forEach(c => {
        if (c !== code)
            newCodes[c] = true;
    });

    return newCodes;
}